import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConfidenceMeter from '../common/ConfidenceMeter';

const SCENARIOS = [
  { key: 'Mass Casualty', icon: '💀', description: 'Multi-vehicle accident with 50+ casualties' },
  { key: 'Building Fire', icon: '🔥', description: 'High-rise commercial fire with trapped occupants' },
  { key: 'Flash Flood', icon: '🌊', description: 'Rapid urban flooding from storm surge' },
  { key: 'Power Outage', icon: '⚡', description: 'Grid failure affecting hospital and emergency services' },
];

export default function SimulationModal({ onClose, onRun, loading, result, scenario, setScenario }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(8px)' }}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.92, y: 20 }}
        className="glass-card w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6 relative"
        style={{ borderColor: '#FF3B3B30' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-lg"
          aria-label="Close simulation modal"
        >
          ✕
        </button>

        <h2 className="font-display font-bold text-xl text-crimson mb-1">
          🎮 Digital Twin Simulator
        </h2>
        <p className="text-gray-400 text-sm mb-5">
          AI-powered crisis scenario modeling · Bhubaneswar, Odisha
        </p>

        {/* Scenario selector */}
        {!result && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-5">
              {SCENARIOS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setScenario(s.key)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    scenario === s.key
                      ? 'border-crimson bg-crimson/10'
                      : 'border-navy-border hover:border-gray-500'
                  }`}
                >
                  <div className="text-2xl mb-1">{s.icon}</div>
                  <div className="font-bold text-sm text-white">{s.key}</div>
                  <div className="text-xs text-gray-400 mt-1">{s.description}</div>
                </button>
              ))}
            </div>

            <button
              onClick={onRun}
              disabled={loading}
              className="w-full py-3 rounded-xl font-display font-bold text-navy bg-crimson hover:bg-crimson-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="spinner w-4 h-4" />
                  Running AI Simulation...
                </>
              ) : (
                `▶ Simulate — ${scenario}`
              )}
            </button>
          </>
        )}

        {/* Results */}
        {result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
            {/* Summary row */}
            <div className="grid grid-cols-3 gap-4">
              <div className="glass-card p-3 text-center">
                <ConfidenceMeter score={result.successProbability} size={60} label="Success" />
              </div>
              <div className="glass-card p-3 col-span-2 space-y-2">
                <div>
                  <span className="text-xs text-gray-400">Duration</span>
                  <div className="font-bold text-white">{result.totalDuration}</div>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Estimated Casualties</span>
                  <div className="text-sm text-amber font-semibold">{result.casualtyEstimate}</div>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Primary Evacuation Route</span>
                  <div className="text-xs text-gray-300">{result.evacuationRoute}</div>
                </div>
              </div>
            </div>

            {/* Phases */}
            <div>
              <div className="text-xs font-mono text-electric mb-3 tracking-wider">
                RESPONSE PHASES
              </div>
              <div className="space-y-3">
                {result.phases?.map((p, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.12 }}
                    className="p-4 rounded-xl bg-navy border border-navy-border"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-electric text-sm">
                        Phase {i + 1}: {p.phase}
                      </span>
                      <span className="text-xs font-mono text-gray-400 bg-navy-border px-2 py-0.5 rounded">
                        {p.duration}
                      </span>
                    </div>
                    <ul className="space-y-1 mb-2">
                      {p.actions?.map((a, j) => (
                        <li key={j} className="text-sm text-gray-300 flex gap-2">
                          <span className="text-electric flex-shrink-0">→</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="text-xs text-amber flex items-center gap-1">
                      <span>🚒</span>
                      <span>Resources: {p.resources}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Critical decision points */}
            {result.criticalDecisionPoints?.length > 0 && (
              <div>
                <div className="text-xs font-mono text-crimson mb-2 tracking-wider">
                  ⚠️ CRITICAL DECISION POINTS
                </div>
                <div className="space-y-1">
                  {result.criticalDecisionPoints.map((cp, i) => (
                    <div
                      key={i}
                      className="text-sm text-gray-300 py-2 px-3 rounded-lg bg-crimson/5 border border-crimson/20 flex items-start gap-2"
                    >
                      <span className="text-crimson font-bold flex-shrink-0">{i + 1}.</span>
                      {cp}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Re-run button */}
            <button
              onClick={() => { setScenario(scenario); onRun(); }}
              className="w-full py-2 rounded-xl text-sm font-semibold border border-navy-border text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
            >
              ↺ Run Another Scenario
            </button>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
